import { useQueryClient } from '@tanstack/react-query';
import { Store } from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { usePermissions } from '@/hooks/usePermissions';
import { useActiveBranches, useCurrentBranch } from '../hooks/useBranches';
import { setSelectedBranchId } from '../branch-selection';

/**
 * Local en el que se está trabajando.
 * - Admin con más de un local: puede cambiar de local (caja, ventas y terminal de ese local).
 * - Cajero: ve el nombre de su local, sin poder cambiarlo.
 * Con un solo local no se muestra nada.
 */
export function BranchSwitcher({ collapsed }: { readonly collapsed?: boolean }) {
    const { isAdmin } = usePermissions();
    const queryClient = useQueryClient();
    const { data: current } = useCurrentBranch();
    const { data: branches } = useActiveBranches();

    if (collapsed || !current) return null;

    if (!isAdmin || branches.length <= 1) {
        // Cajero: solo informativo. Admin con un solo local: no hace falta mostrarlo.
        if (isAdmin) return null;
        return (
            <div className="mt-3 flex items-center justify-center gap-1.5 text-xs text-muted-foreground" title="Local">
                <Store className="h-3.5 w-3.5" />
                <span className="truncate font-medium">{current.name}</span>
            </div>
        );
    }

    const handleChange = async (id: string) => {
        if (id === current.id) return;
        setSelectedBranchId(id);
        // Todo lo que depende del local (caja, ventas, catálogo) se vuelve a pedir
        await queryClient.invalidateQueries();
        const name = branches.find((b) => b.id === id)?.name;
        toast.success(`Trabajando en ${name ?? 'el local elegido'}`);
    };

    return (
        <div className="mt-3">
            <Select value={current.id} onValueChange={handleChange}>
                <SelectTrigger className="h-8 text-xs" aria-label="Local">
                    <Store className="h-3.5 w-3.5 mr-1.5 shrink-0" />
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {branches.map((b) => (
                        <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}
